import { Subject } from "rxjs";
import { BaseGroupType, GroupViewBy } from "src/AngularJS/Models/group-type.model";
import { Group } from "src/AngularJS/Models/group.model";
import { GroupSelectorDTO } from "src/AngularJS/Reusables/group-selector.models";
import { AttributeSelectorModel, AttributeType } from "src/shared/models/attribute-selector.models";
import { DashboardModuleEagerLoad, DashboardModuleResponsive, DashboardModuleResponsiveInstance } from "src/shared/models/module-info.model";
import { DOW, ModuleManagerDateRangeInfo, ModuleManagerGenericSettingsControls, ModuleManagerMetricInfo, TimeInterval } from "src/shared/models/module-manager/module-manager-settings.models";
import { GroupTypeInfo } from "src/shared/models/slideout-settings-manager";
import { LoginApiModel } from "./login.model";
import { AssignmentPermissions } from "src/AngularJS/Dashboard/dashboard-assignment.model";


export enum WorkspaceNewTabViewMode {
    Landing = 0,
    New = 1,
    Existing = 2,
    Templates = 3,
}

export enum WorkspaceTemplateViewMode {
    Landing = 0,
    NewTemplate = 1,
    NewSystemTemplate = 2,
}

export enum Status {
    Draft = 0,
    Published_Draft = 1,
    Published = 2,
    Unpublished = 3,
}

export interface Dashboard {
    // from C# BLL
    DashboardID: number;
    RequestingLoginID: number;
    DashboardName: string;
    DashboardDescription: string;
    Position: number;
    DashboardKey: string;
    RelatedDashboardID: number;
    DashboardGroup: string;
    AllowAlterDateOnShared: boolean;
    Responsive: boolean;
    LastViewDate: string;
    TemporaryDashboard: boolean;
    TimeZoneInfoName: string;
    IsCXOne?: boolean;
    CreatorID: number;
    ReportTemplatePermission: string;
    IsCustomReport: boolean;
    IsReport?: boolean;

    // added by front-end
    states?: DashboardFrontendStates;
    NeedConfirmationToClose?: boolean;
    isFiltersApplyButtonEnabled?: boolean;
    publishPending?: boolean;
    isShared?: boolean;
    sharedOwner?: boolean;
    writeAccess?: boolean;
    readGroupAccess?: boolean;
    readControlAccess?: boolean;
    readAccess?: boolean;
    canReset?: boolean;
}

export interface DashboardFrontendStates {
    dashboardModules: {
        loading: boolean;
        success: boolean;
    };

    publish: {
        loading: boolean;
        success: boolean;
    };
}

export interface DashboardResponsiveApiModel {
    dashboardKey: string;
    dashboard: Dashboard;
    availableModules: DashboardModuleResponsive[];
    blockedModules: DashboardModuleResponsive[];
    modules: DashboardModuleResponsiveInstance[];
    modulesEagerLoad: { [moduleKey: string]: DashboardModuleEagerLoad };
    SharedReadAccess: boolean;
    SharedReadGroupAccess: boolean;
    SharedWriteAccess: boolean;
    SharedReadControlAccess: boolean;
    SharedOwner: boolean;
}

export interface DashboardResponsiveApiModelSparse extends Dashboard {
    IsSubscribed: boolean;
    SharedKey: string;
    SharedReadAccess: boolean;
    SharedReadControlAccess: boolean;
    SharedWriteAccess: boolean;
    SharedTemplateAccess: boolean;
    SharedPublicAccess: boolean;
    MasterGroupName: string;
    CreatedBy: string;
    SharedBy: string;
    ModifiedBy: string;
    LastModifiedDate: string;
    NumRecipientLogins: number;
    NumSubscribed: number;
    RecipientLogins: DashboardRecipientLogin[];
    RecipientGroups: { [groupId: number]: DashboardRecipientGroup };
    RecipientAttributes: { [attributeValueId: number]: DashboardRecipientAttribute };
    modulesEagerLoad: { [moduleKey: string]: DashboardModuleEagerLoad };
    IsFavorite: boolean;
    IsCXOne: boolean;
    IsPublic: boolean;
    IsSharedURL: boolean;
    Owners: string[];
}

export interface DashboardAccessOptions {
    dashboard: DashboardResponsiveApiModelSparse;
}

/**
 * Most slim version of dashboards needed to populate the dashboard selection dropdown on the dashboard header.
 * Represents a single dashboard in the list of dashboards on the dashboard header.
 */
export interface DashboardSelectionListApiModel {
    DashboardID: number;
    DashboardKey: string;
    DashboardName: string;
    DashboardDescription: string;
    IsFavorite: boolean;
    RelatedDashboardID: number;
    IsReport?: boolean;
}

export interface DashboardsResponsiveExistingApiModel {
    Dashboards: DashboardResponsiveApiModelSparse[];
    Groups: string[];
    Logins: Map<number, DashboardRecipientLogin>;
}

export interface DashboardGroupStateDto
{
    GroupName: string;
    Collapsed: boolean;
    Position: number;
    TableState: string;
}

export interface SystemTemplateWto {
    SystemTemplateID: number;
    Integrations: string;
    IntegrationID: number;
    Personas: string;
    UserType: string;
    SystemTemplateName: string;
    SystemTemplateDesc: string;
    SystemTemplateKey: string;
    SystemTemplateGroup: string;
    LastUsedDate: string;
    LastModifiedDate: string;
    LastDownloadDate: string;
    Status: Status;
    LastModifiedBy: string;
    SelectedDomains: string;
    PermissionName: string;
}

export interface ActiveDashboardModel {
    index: number;
    key: string;
}

export interface DashboardRecipientGroup {
    Group: Group;
    Profiles: DashboardRecipientProfileApiModel[];
}

export interface DashboardRecipientAttribute {
    Attribute: AgentAttributeValueModel;
    Profiles: DashboardRecipientProfileApiModel[];
}

export interface DashboardShareTypeFlags {
    read: boolean;
    readTheirs: boolean;
    readGroup: boolean;
    readControl: boolean;
    write: boolean;
    writeTheirs: boolean;
    template: boolean;
}

export interface DashboardRecipientProfileApiModel extends ProfileApiModel, DashboardShareTypeFlags {
    relatedLogins: LoginApiModel[];
    relatedLoginIds: number[];
}

export interface AgentAttributeValueModel {
    Active: boolean;
    AddedBy: number;
    AgentAttributeID: number;
    AttributeValueID: number;
    AttributeValueName: string;
    CompanyID: number;
    DateAdded: string;
    LastModifiedBy: number;
    LastModifiedDate: string;
    MaxValue: number;
    MaxValueUnit: number;
    MinValue: number;
    MinValueUnit: number;
}
export interface ProfileApiModel {
    ProfileID: number;
    CompanyID: number;
    ProfileName: string;
    GroupID: number;
    GroupLevel: number;
    GroupType: number;
    GroupTypeStr: string;
}

export type DashboardRecipientLogin = LoginApiModel & DashboardShareTypeFlags;

export interface ProfileShareModel extends ProfileApiModel {
    Permissions: AssignmentPermissions;
}

export interface IDashboardModules{ // DashboardsModulesCollection from ResponsiveDashboardsController.cs
    DashboardKey: string;
    Modules: DashboardModuleResponsiveInstance[]; // DashboardModuleResponsiveInstance
}

export interface IDashboardAssignments{ // DashboardAssignments from ResponsiveDashboardsController.cs
    DashboardKey: string;
    RecipientLogins: any; // HashSet<DashboardRecipientLogin>
    RecipientGroups: any; // Dictionary<int, DashboardRecipientGroup>
    RecipientAttributes: any; // Dictionary<int, DashboardRecipientAttribute>
    NumRecipientLogins: number; // Shared to
}

export interface IDashboardManager{ // Interface to convert the JS dashboardManager into TS
    requestPublishNeeded(): void;
    reloadDashboard(): void;
}

export type SaveDashboardModalData = {
    dashboardName: string;
    description: string;
    folder: string;
    key: string;
    iscxone?: boolean;
    workspaceKey?: string;
};

/** TypeScript version of Module Settings Manager that was modified to work with dashboards. */
export class DashboardFilterManager{
    empSelectorDTO: GroupSelectorDTO;
    recSelectorDTO: GroupSelectorDTO;

    userAttributesModel: AttributeSelectorModel;
    dataAttributesModel: AttributeSelectorModel;

    selectedToggle: BaseGroupType = BaseGroupType.Employee;
    selectedLevelName: string;
    selectedRecCount: number;
    selectedEmpCount: number;

    empFilterLevelName: string;
    recFilterLevelName: string;

    selectedMetrics: ModuleManagerMetricInfo[];

    selectedDateRange: ModuleManagerDateRangeInfo;
    isTimeSelectorEnabled: boolean;
    invertTimeSelector: boolean;
    selectedDaysOfWeek: DOW;
    minTime: number;
    maxTime: number;
    intervalAmt: number;
    intervalType: TimeInterval;

    onAttributeReloaded = new Subject<AttributeSelectorModel>();
    onSelectedDateRangeChanged = new Subject<ModuleManagerDateRangeInfo>(); // reload groups
    onSelectedEmpGroupsChanged = new Subject<GroupSelectorDTO>(); // reload rec
    onSelectedRecGroupsChanged = new Subject<GroupSelectorDTO>(); // reload emp // reload metric
    onSelectedGroupViewByChanged = new Subject<GroupViewBy>(); // reload metric
    onSettingsCancelled = new Subject<void>();
    onSettingsApplied = new Subject<void>();
    beforeSettingsApplied = new Subject<void>();

    private _empGroupTypes: GroupTypeInfo[] = [];
    get empGroupTypes() { return this._empGroupTypes; }
    set empGroupTypes(v: GroupTypeInfo[]) {
        this._empGroupTypes = v;
        //this.selectedEmpCount = this.getSelectedCount(BaseGroupType.Employee);
    }

    private _recGroupTypes: GroupTypeInfo[] = [];
    get recGroupTypes() { return this._recGroupTypes; }
    set recGroupTypes(v: GroupTypeInfo[]) {
        this._recGroupTypes = v;
        //this.selectedEmpCount = this.getSelectedCount(BaseGroupType.Recording);
    }

    /** Create Default Filter Settings */
    constructor() {
        // Datetime
        this.selectedDateRange = ModuleManagerDateRangeInfo.fromObject({
            name: 'Today',
            displayName: 'Today',
            utcOffset: 0,
        });
        this.selectedDaysOfWeek = 127 as DOW;
        this.isTimeSelectorEnabled = false;
        this.invertTimeSelector = false;
        this.minTime = 0;
        this.maxTime = 0;

        this.intervalAmt = 1;
        this.intervalType = 2;

        this.empSelectorDTO = {
            ApplicationID: 0,
            PageName: "",
            SelectedGroupLevel: 1,
            SelectedGroupType: BaseGroupType.Employee,
            SelectedMinLevel: 1,
            SelectedMaxLevel: 3,
            _SelectedGroup1IDList: [0],
            _SelectedGroup2IDList: [0],
            _SelectedGroup3IDList: [0],
            _SelectedGroup4IDList: [0],
            _SelectedGroup5IDList: [0],
            ModuleRender: false,
            ShiftRender: false,
            AllSelected: false,
            toggleGroupData: false
        };

        this.recSelectorDTO = {
            ApplicationID: 0,
            PageName: "",
            SelectedGroupLevel: 1,
            SelectedGroupType: BaseGroupType.Recording,
            SelectedMinLevel: 1,
            SelectedMaxLevel: 4,
            _SelectedGroup1IDList: [0],
            _SelectedGroup2IDList: [0],
            _SelectedGroup3IDList: [0],
            _SelectedGroup4IDList: [0],
            _SelectedGroup5IDList: [0],
            ModuleRender: false,
            ShiftRender: false,
            AllSelected: false,
            toggleGroupData: false
        };

        this.selectedLevelName = "";
        this.selectedToggle = BaseGroupType.Employee;

        this.dataAttributesModel = {
            GroupedCategoryID: 0,
            SelectedCategories: [],
            Type: AttributeType.Data
        };

        this.userAttributesModel = {
            GroupedCategoryID: 0,
            SelectedCategories: [],
            Type: AttributeType.User
        };
    }

    /** Convert string to simplified settings object then apply settings */
    public static fromString(json: string) {
        try {
            const simplified = JSON.parse(json);
            const filterManager = new DashboardFilterManager();

            filterManager.empSelectorDTO = simplified.empSelectorDTO;
            filterManager.recSelectorDTO = simplified.recSelectorDTO;
            filterManager.userAttributesModel = simplified.userAttributesModel;
            filterManager.dataAttributesModel = simplified.dataAttributesModel;
            filterManager.selectedToggle = simplified.selectedToggle;
            filterManager.selectedLevelName = simplified.selectedLevelName;
            filterManager.selectedRecCount = simplified.selectedRecCount;
            filterManager.selectedEmpCount = simplified.selectedEmpCount;
            filterManager.empFilterLevelName = simplified.empFilterLevelName;
            filterManager.recFilterLevelName = simplified.recFilterLevelName;
            filterManager.selectedDateRange = ModuleManagerDateRangeInfo.fromObject(simplified.selectedDateRange);
            filterManager.isTimeSelectorEnabled = !!simplified.isTimeSelectorEnabled;
            filterManager.invertTimeSelector = simplified.invertTimeSelector;
            filterManager.selectedDaysOfWeek = simplified.selectedDaysOfWeek;
            filterManager.minTime = simplified.minTime;
            filterManager.maxTime = simplified.maxTime;
            filterManager.intervalAmt = simplified.intervalAmt;
            filterManager.intervalType = simplified.intervalType;

            return filterManager;
        } catch {
            return null;
        }
    }

    /** Simplify Settings object then convert to json string */
    public toString(){
        const simplified = {
            empSelectorDTO: this.empSelectorDTO,
            recSelectorDTO: this.recSelectorDTO,
            userAttributesModel: this.userAttributesModel,
            dataAttributesModel: this.dataAttributesModel,
            selectedToggle: this.selectedToggle,
            selectedLevelName: this.selectedLevelName,
            selectedRecCount: this.selectedRecCount,
            selectedEmpCount: this.selectedEmpCount,
            empFilterLevelName: this.empFilterLevelName,
            recFilterLevelName: this.recFilterLevelName,
            selectedDateRange: this.selectedDateRange,
            isTimeSelectorEnabled: this.isTimeSelectorEnabled,
            invertTimeSelector: this.invertTimeSelector,
            selectedDaysOfWeek: this.selectedDaysOfWeek,
            minTime: this.minTime,
            maxTime: this.maxTime,
            intervalAmt: this.intervalAmt,
            intervalType: this.intervalType
        };

        return JSON.stringify(simplified);
    }

    /**
     * Finds and returns the number of groups selected at the selected level.
     * If all levels has All, return 0
     *
     * @param groupType Either Employee or Recording
     */
    public getSelectedCount(groupType: BaseGroupType) {
        const dto = this.getSelectorByType(groupType);
        if (groupType === this.selectedToggle) { // is set to "View by"
            const groupsForLevel: number[] = dto[`_SelectedGroup${dto.SelectedGroupLevel}IDList`];
            if (groupsForLevel.length === 0 || (groupsForLevel.length === 1 && groupsForLevel[0] === 0))
                return 0; // ALL
            else
                return groupsForLevel.length;
        } else { // just a filter
            // find lowest level with selections
            const groupTypes = (groupType === BaseGroupType.Employee) ? this.empGroupTypes : this.recGroupTypes;
            let filterLevelName = '';
            for (let i = dto.SelectedMinLevel; i <= dto.SelectedMaxLevel; i++) {
                const groupsForLevel: number[] = dto[`_SelectedGroup${i}IDList`];
                const currGroupLevel = groupTypes.find(gt => gt.level === i);
                filterLevelName = currGroupLevel?.name || '';
                if (groupsForLevel.length === 0 || (groupsForLevel.length === 1 && groupsForLevel[0] === 0)) {
                    continue; // ALL
                } else {
                    return groupsForLevel.length; // found a selection
                }
            }

            // all levels had ALL
            filterLevelName = groupTypes[groupTypes.length - 1]?.name; // lowest level

            switch (groupType) {
                case BaseGroupType.Employee:
                    this.empFilterLevelName = filterLevelName;
                    break;
                case BaseGroupType.Recording:
                    this.recFilterLevelName = filterLevelName;
                    break;
            }

            return 0;
        }
    }

    /**
     * Get group selector DTO based on the group type
     *
     * @param type Either Employee or Recording
     */
    public getSelectorByType(type: BaseGroupType) {
        if (type === BaseGroupType.Employee)
            return this.empSelectorDTO;
        else if (type === BaseGroupType.Recording)
            return this.recSelectorDTO;

        return null;
    }
}

export interface NewModule {
    genericSettings: ModuleManagerGenericSettingsControls;
    newKey: string;
}
